import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, SearchX, MousePointerClick, TrendingUp, EyeOff, LineChart } from 'lucide-react';
import { sopApi } from '../api';

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const Stat = ({ label, value, hint, tone = 'text-slate-100' }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
    <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const Panel = ({ title, icon: Icon, hint, children, empty }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-700">
      <h3 className="text-sm font-semibold text-slate-200 inline-flex items-center gap-2">
        <Icon className="w-4 h-4 text-indigo-400" /> {title}
      </h3>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
    {children || <p className="px-4 py-6 text-sm text-slate-500 text-center">{empty}</p>}
  </div>
);

const SearchInsights = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    sopApi.insights(days)
      .then((r) => { setData(r.data); setError(''); })
      .catch((err) => setError(err.message || 'Failed to load insights'))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading insights…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">{error}</div>
      </div>
    );
  }

  const totals = data.totals || {};
  const totalSearches = Number(totals.totalSearches || 0);
  const totalZero = Number(totals.totalZero || 0);
  const totalClicks = Number(totals.totalClicks || 0);
  const pct = (n) => (totalSearches ? `${Math.round((n / totalSearches) * 100)}%` : '—');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Search Insights</h2>
          <p className="text-sm text-slate-400 mt-1">
            What people looked for, and whether they found it.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-md p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setDays(r.value)}
              className={`px-3 py-1.5 text-xs rounded ${days === r.value
                ? 'bg-indigo-600 text-white'
                : 'text-slate-400 hover:text-slate-200'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Searches" value={totalSearches} hint={`Last ${days} days`} />
        <Stat
          label="Found nothing"
          value={pct(totalZero)}
          hint={`${totalZero} searches returned zero results`}
          tone={totalZero > 0 ? 'text-amber-300' : 'text-slate-100'}
        />
        <Stat
          label="Opened a result"
          value={pct(totalClicks)}
          hint={`${totalClicks} searches led to an SOP`}
          tone={totalClicks > 0 ? 'text-emerald-300' : 'text-slate-100'}
        />
      </div>

      <Panel
        title="Searches that found nothing"
        icon={SearchX}
        hint="Each of these is an SOP nobody has written yet — or one tagged with different words."
        empty="No dead-end searches in this period."
      >
        {data.zero?.length > 0 && (
          <div className="divide-y divide-slate-700">
            {data.zero.map((row) => (
              <div key={row.query} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-slate-200 truncate">&ldquo;{row.query}&rdquo;</span>
                <span className="text-xs text-amber-300 flex-shrink-0 tabular-nums">
                  {row.searches}&times;
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Found results, but nobody clicked"
        icon={MousePointerClick}
        hint="The library has something, but it doesn't look like the answer. Usually a title or tagging problem."
        empty="Nothing here — searches are converting."
      >
        {data.noClick?.length > 0 && (
          <div className="divide-y divide-slate-700">
            {data.noClick.map((row) => (
              <div key={row.query} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-slate-200 truncate">&ldquo;{row.query}&rdquo;</span>
                <span className="text-xs text-slate-400 flex-shrink-0 tabular-nums">{row.searches}&times;</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Most common searches" icon={LineChart} empty="No searches logged yet.">
        {data.top?.length > 0 && (
          <div className="divide-y divide-slate-700">
            {data.top.map((row) => (
              <div key={row.query} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-slate-200 truncate">&ldquo;{row.query}&rdquo;</span>
                <div className="flex items-center gap-3 text-xs flex-shrink-0 tabular-nums">
                  <span className="text-slate-400">{row.searches} searches</span>
                  {Number(row.zeroResults) > 0 && (
                    <span className="text-amber-300">{row.zeroResults} empty</span>
                  )}
                  <span className="text-emerald-300">{row.clicks} opened</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most viewed SOPs" icon={TrendingUp} empty="No views recorded yet.">
          {data.popular?.length > 0 && (
            <div className="divide-y divide-slate-700">
              {data.popular.map((row) => (
                <Link
                  key={row.id}
                  to={`/tiles/ksp_sops/view/${row.id}`}
                  className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-700/40"
                >
                  <span className="text-sm text-slate-200 truncate">{row.title}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0 tabular-nums">{row.viewCount}</span>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Never opened"
          icon={EyeOff}
          hint="Redundant, or undiscoverable."
          empty="Every SOP has been opened at least once."
        >
          {data.neglected?.length > 0 && (
            <div className="divide-y divide-slate-700">
              {data.neglected.map((row) => (
                <Link
                  key={row.id}
                  to={`/tiles/ksp_sops/view/${row.id}`}
                  className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-700/40"
                >
                  <span className="text-sm text-slate-200 truncate">{row.title}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">0 views</span>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default SearchInsights;
